/* eslint-disable no-unused-expressions */
import { getDataFromCookie } from 'aster-foundation/core/common/utils/cookieHelper';
import capitalize from 'lodash/capitalize';
import moment from 'moment';
import { convertDateToBackendServer, convertDateToISOStandard, formatDate } from '../../../Common/Utility/common-utils';

const getReqParams = (
  form,
  mobileWithCode,
  getOCRStatus,
  type,
  frontFileName,
  backFileName,
  flag,
  stateCode,
  patientId,
  isEditFamily,
  isNotCompleteProfileScreen,
) => {
  const {
    base64Images: {
      profile: { isChanged: isProfileImageChanged, img: base64ProfileImg },
      emiratesID: { frontImg: base64FrontImg, backImg: base64BackImg },
      passport: { frontImg: base64forFrontPassport },
    },
    nationality: { val: nationality },
    visaStatus,
    emiratesID: { val: emiratesID, expiryDate: emiratesExpDate },
    passportNum: { val: passportNum, expiryDate: passportExpDate },
    name: { firstName, lastName, gender, relationship, title },
    area: { name: areaName },
    dob: { val: birthDate, ocrDob, isEditable },
    email,
    city: { id: cityId },
    country: { id: countryID },
    terms,
  } = form;

  const getOcrDob = () => {
    if (isEditable) {
      return convertDateToBackendServer(ocrDob);
    }

    if (!isEditable && isNotCompleteProfileScreen) {
      return convertDateToBackendServer(birthDate);
    }

    return null;
  };

  const params = {
    resourceType: 'Patient',
    id: patientId,
    photo: [
      isProfileImageChanged
        ? {
            data: base64ProfileImg?.split(',')?.[1] || null,
            title: 'Profile_img.jpg',
          }
        : {},
    ],
    name: [
      {
        family: lastName,
        given: [firstName],
        prefix: [title],
      },
    ],
    telecom: [
      {
        system: 'phone',
        value: mobileWithCode,
      },
      {
        system: 'email',
        value: email,
      },
    ],
    gender,
    birthDate: convertDateToBackendServer(birthDate),
    address: [
      {
        text: areaName,
        city: cityId,
        state: stateCode,
        country: countryID,
      },
    ],
    extension: [
      {
        url: 'ocrDob',
        valueString: getOcrDob(),
      },
      {
        url: 'nationality',
        valueCoding: {
          code: nationality,
        },
      },
      {
        url: 'parentUserId',
        valueString: patientId,
      },
      {
        url: 'relationShip',
        valueString: relationship,
      },
      {
        url: 'documentList',
        extension: [
          {
            url: visaStatus === 'emiratesID' ? 'emiratesId' : 'passport',
            extension: [
              {
                url: 'frontSide',
                valueAttachment: {
                  data:
                    visaStatus === 'emiratesID'
                      ? base64FrontImg?.split(',')?.[1]
                      : base64forFrontPassport?.split(',')?.[1],
                  title: frontFileName,
                },
              },
              {
                url: 'id',
                valueIdentifier: {
                  value: visaStatus === 'emiratesID' ? emiratesID : passportNum,
                  period: {
                    end: visaStatus === 'emiratesID' ? formatDate(emiratesExpDate) : formatDate(passportExpDate),
                  },
                },
              },
              {
                url: 'isEmiratesIdScanned',
                valueBoolean: getOCRStatus(),
              },
              visaStatus === 'emiratesID'
                ? {
                    url: 'backSide',
                    valueAttachment: base64BackImg
                      ? {
                          data: base64BackImg?.split(',')?.[1] || null,
                          title: backFileName,
                        }
                      : {},
                  }
                : {},
            ],
          },
        ],
      },
      {
        url: 'visaStatus',
        valueString: visaStatus === 'passport' ? 'Tourist' : 'Residence',
      },
      {
        url: 'isTermsAccepted',
        valueBoolean: type === 'addSidebar' ? true : terms,
      },
      {
        url: 'isConsentAccepted',
        valueBoolean: !!terms,
      },
      {
        url: 'azureId',
        valueString: getDataFromCookie('userId'),
      },
      {
        url: 'isYOBDifferentInEID',
        valueBoolean: flag,
      },
    ].filter((item) => {
      if (
        type === 'completeProfile' ||
        (type === 'edit' && !isEditFamily) ||
        type === 'completeProfileSecondary' ||
        type === 'completeProfileOther'
      ) {
        return item.url !== 'relationShip';
      }

      if (type === 'edit') {
        return !['isTermsAccepted', 'isConsentAccepted'].includes(item.url);
      }

      if (type === 'completeProfile' || type === 'completeProfileSecondary' || type === 'completeProfileOther') {
        return item.url !== 'parentUserId';
      }

      if (type === 'add' || type === 'addSidebar') {
        return item.url !== 'azureId';
      }

      return true;
    }),
  };

  if (type === 'add') {
    delete params.id;
  }

  if (type === 'completeProfileOther') {
    delete params.address;
  }

  return params;
};

const handleEmiratesDateStates = (
  idVal,
  visaStatus,
  expiryDate,
  form,
  imgSide,
  base64FrontImg,
  base64BackImg,
  isNotCompleteProfileScreen,
  key,
  dob,
  setForm,
  setErrors,
  errors,
  setOcr,
  ocr,
) => {
  setForm({
    ...form,
    emiratesID: {
      ...form?.emiratesID,
      val: visaStatus === 'emiratesID' ? idVal : form?.emiratesID?.val,
      expiryDate: visaStatus === 'emiratesID' ? expiryDate : form.emiratesID.expiryDate,
    },
    passportNum: {
      ...form?.passportNum,
      val: visaStatus === 'passport' ? idVal : form?.passportNum?.val,
      expiryDate: visaStatus === 'passport' ? expiryDate : form.passportNum.expiryDate,
    },
    base64Images: {
      ...form.base64Images,
      [key]: {
        ...form.base64Images[key],
        [`${imgSide}Img`]: imgSide === 'front' ? base64FrontImg : base64BackImg,
      },
    },
    dob: {
      ...form.dob,
      ocrDob: dob ? new Date(convertDateToISOStandard(moment(dob, 'DD/MM/YYYY'))) : form.dob.ocrDob,
      val:
        dob && isNotCompleteProfileScreen
          ? new Date(convertDateToISOStandard(moment(dob, 'DD/MM/YYYY')))
          : form.dob.val,
      isEditable: true,
    },
  });

  const isEmiratesId = visaStatus === 'emiratesID';
  const expiryDateLabel = isEmiratesId ? 'emiratesExpDate' : 'passportExpDate';
  const expiryDateError = isEmiratesId ? 'Your emirates Id is expired' : 'Your passport is expired';

  // eslint-disable-next-line no-use-before-define
  if (expiryDate && !isFutureDate(expiryDate)) {
    setErrors({ ...errors, [expiryDateLabel]: expiryDateError, [`base64${capitalize(imgSide)}Img`]: null });
  } else {
    setErrors({ ...errors, [expiryDateLabel]: null, [`base64${capitalize(imgSide)}Img`]: null });
  }

  setOcr({
    ...ocr,
    ocrId: idVal,
    isScanned: true,
    ocrDate: new Date(expiryDate),
    frontHasExpDate: imgSide === 'front' && !!expiryDate,
    backHasExpDate: imgSide !== 'front' && !!expiryDate,
  });
};

const setandShowModalProps = (value, setModal, visaStatus, eventText, showPopUp) => {
  if (showPopUp) {
    setModal(true);
  }

};

const formatEmiratesResponse = (
  response,
  side,
  setOcr,
  setErrors,
  base64FrontImg,
  base64BackImg,
  setForm,
  errors,
  setModal,
  eventText,
  form,
  ocr,
  imgSide,
  key,
  isNotCompleteProfileScreen,
) => {
  const visaStatus = form?.visaStatus;
  let ocrSuccess = false;
  if (response?.emiratesId && side === 'Front') {
    ocrSuccess = true;
    const idVal = response?.emiratesId;
    const expiryDate = response?.emairateExpiryDate
      ? convertDateToISOStandard(moment(response?.emairateExpiryDate, 'DD/MM/YYYY'))
      : '';
    handleEmiratesDateStates(
      idVal,
      visaStatus,
      expiryDate,
      form,
      imgSide,
      base64FrontImg,
      base64BackImg,
      isNotCompleteProfileScreen,
      key,
      response?.dob,
      setForm,
      setErrors,
      errors,
      setOcr,
      ocr,
    );
  }

  if ((response?.emairateExpiryDate || response?.dob) && side === 'Back') {
    ocrSuccess = true;
    const idVal = visaStatus === 'emiratesID' ? form?.emiratesID?.val : form?.passportNum?.val;
    const expiryDate = response?.emairateExpiryDate
      ? convertDateToISOStandard(moment(response?.emairateExpiryDate, 'DD/MM/YYYY'))
      : '';
    handleEmiratesDateStates(
      idVal,
      visaStatus,
      expiryDate,
      form,
      imgSide,
      base64FrontImg,
      base64BackImg,
      isNotCompleteProfileScreen,
      key,
      response?.dob,
      setForm,
      setErrors,
      errors,
      setOcr,
      ocr,
    );
  }
  if (visaStatus === 'emiratesID') {
    const idVal = response?.emiratesId || form?.emiratesID?.val;
    const expiryDate = response?.emairateExpiryDate || form?.emiratesID?.expiryDate;
    if (
      idVal &&
      idVal?.length > 10 &&
      (base64BackImg || base64BackImg?.length > 0) &&
      expiryDate &&
      expiryDate?.length > 0 &&
      (base64FrontImg || base64FrontImg?.length > 0)
    ) {
      ocrSuccess = true;
      setandShowModalProps(ocrSuccess, setModal, visaStatus, eventText, true);
    } else {
      ocrSuccess = false;
      setandShowModalProps(ocrSuccess, setModal, visaStatus, eventText, false);
    }
  } else {
    setandShowModalProps(ocrSuccess, setModal, visaStatus, eventText, true);
  }
};

const getAge = (date) => {
  const dateString = date;
  if (dateString !== '') {
    const today = new Date();
    const bDate = new Date(dateString);
    let age = today.getFullYear() - bDate.getFullYear();
    let m = today.getMonth() - bDate.getMonth();
    let da = today.getDate() - bDate.getDate();
    if (m < 0 || (m === 0 && today.getDate() < bDate.getDate())) {
      age--;
    }
    if (m < 0) {
      m += 12;
    }
    if (da < 0) {
      da += 30;
    }
    return age;
  }
  return '';
};

const isFutureDate = (date) => {
  const today = new Date().getTime();
  const idate = new Date(date).getTime();

  return today - idate < 0;
};

const relationshipData = [
  {
    text: 'Father',
    value: '1',
    gender: 'male',
  },
  {
    text: 'Mother',
    value: '2',
    gender: 'female',
  },
  {
    text: 'Daughter',
    value: '2',
    gender: 'female',
  },
  {
    text: 'Son',
    value: '2',
    gender: 'male',
  },
  {
    text: 'Wife',
    value: '2',
    gender: 'female',
  },
  {
    text: 'Husband',
    value: '2',
    gender: 'male',
  },
  {
    text: 'Brother',
    value: '2',
    gender: 'male',
  },
  {
    text: 'Sister',
    value: '2',
    gender: 'female',
  },
];

const defaultFormState = {
  terms: false,
  base64Images: {
    profile: {
      img: null,
      isDeletable: true,
      isDefault: true,
      isChanged: false,
    },
    emiratesID: {
      frontImg: null,
      backImg: null,
      isDeletable: true,
    },
    passport: {
      frontImg: null,
      isDeletable: false,
    },
  },
  name: {
    firstName: '',
    lastName: '',
    isEditable: true,
    title: null,
    relationship: 'Select Relationship',
    gender: null,
  },
  mobile: {
    number: '',
    countryCode: '',
    isEditable: false,
  },
  email: null,
  nationality: {
    val: null,
    isEditable: false,
  },
  dob: {
    val: null,
    isEditable: false,
    ocrDob: null,
  },
  visaStatus: 'emiratesID',
  emiratesID: {
    val: '',
    expiryDate: null,
  },
  passportNum: {
    val: '',
    expiryDate: null,
  },
  canEditEIDUploadedImage: false,
  canEditEIDExpDate: false,
  country: {
    id: null,
    name: 'Select Country',
  },
  city: {
    id: null,
    name: 'Select City',
    isEditable: false,
    list: [],
  },
  area: {
    name: null,
    isEditable: false,
  },
  isExpiryDateCalendarVisible: false,
};

const formElementNamesInOrder = [
  'base64ProfileImg',
  'firstName',
  'lastName',
  'title',
  'relation',
  'email',
  'nationality',
  'birthDate',
  'birthYear',
  'status',
  'base64FrontImg',
  'base64BackImg',
  'base64forFrontPassport',
  'emiratesID',
  'passportNum',
  'emiratesExpDate',
  'passportExpDate',
  'country',
  'city',
  'areaName',
  'terms',
];

const fieldsNotInCompleteProfile = [
  'firstName',
  'lastName',
  'title',
  'relation',
  'email',
  'nationality',
  'birthDate',
  'birthYear',
  'terms',
];

const fieldsNotInCompleteProfileOther = [
  'base64ProfileImg',
  'firstName',
  'lastName',
  'title',
  'relation',
  'email',
  'nationality',
  'birthDate',
  'birthYear',
  'country',
  'city',
  'areaName',
  'terms',
];

const fieldsNotInAddFamilySidebar = ['email', 'country', 'city', 'areaName', 'terms'];

const filterFormElementsArrBasedOnType = (type, isFromErxOrInstantGp) => {
  const formElementsArrFiltered = formElementNamesInOrder.filter((element) => {
    if (type === 'completeProfile') {
      return !fieldsNotInCompleteProfile.includes(element);
    }
    if (type === 'addSidebar') {
      return !fieldsNotInAddFamilySidebar.includes(element);
    }
    if (type === 'edit') {
      return !['terms', 'relation'].includes(element);
    }

    if (type === 'completeProfileSecondary') {
      if (!isFromErxOrInstantGp) {
        return ![...fieldsNotInCompleteProfile, 'base64ProfileImg', 'country', 'city', 'areaName'].includes(element);
      }
      return ![...fieldsNotInCompleteProfile, 'base64ProfileImg'].includes(element);
    }

    if (type === 'completeProfileOther') {
      return !fieldsNotInCompleteProfileOther.includes(element);
    }

    return element;
  });
  return formElementsArrFiltered;
};

const keysInOrder = (keys) => {
  const keysInOrderArr = [];
  formElementNamesInOrder.forEach((element) => {
    if (keys.includes(element)) {
      keysInOrderArr.push(element);
    }
  });
  return keysInOrderArr;
};

const filterErrorObjectBasedOnType = (errorObj, type, isFromErxOrInstantGp) => {
  const errorObjFiltered = {};
  keysInOrder(Object.keys(errorObj)).forEach((key) => {
    if (type === 'completeProfile') {
      if (!fieldsNotInCompleteProfile.includes(key)) {
        errorObjFiltered[key] = errorObj[key];
      }
    } else if (type === 'addSidebar') {
      if (!fieldsNotInAddFamilySidebar.includes(key)) {
        errorObjFiltered[key] = errorObj[key];
      }
    } else if (type === 'edit') {
      if (!['terms', 'relation'].includes(key)) {
        errorObjFiltered[key] = errorObj[key];
      }
    } else if (type === 'completeProfileSecondary') {
      if (!isFromErxOrInstantGp) {
        if (![...fieldsNotInCompleteProfile, 'base64ProfileImg', 'country', 'city', 'areaName'].includes(key)) {
          errorObjFiltered[key] = errorObj[key];
        }
      } else if (![...fieldsNotInCompleteProfile, 'base64ProfileImg'].includes(key)) {
        errorObjFiltered[key] = errorObj[key];
      }
    } else if (type === 'completeProfileOther') {
      if (!fieldsNotInCompleteProfileOther.includes(key)) {
        errorObjFiltered[key] = errorObj[key];
      }
    } else {
      errorObjFiltered[key] = errorObj[key];
    }
  });

  return errorObjFiltered;
};

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export {
  defaultFormState,
  fieldsNotInCompleteProfile,
  filterErrorObjectBasedOnType,
  filterFormElementsArrBasedOnType,
  formatEmiratesResponse,
  formElementNamesInOrder,
  getAge,
  getReqParams,
  handleEmiratesDateStates,
  isFutureDate,
  months,
  relationshipData,
  setandShowModalProps,
};
